import React, { useEffect, useMemo, useRef, useState } from 'react';
import { loadExternalScript } from '../../utils/scriptLoader';

const D3_URL = 'https://d3js.org/d3.v7.min.js';
const RANGE_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: '180', label: '6M', days: 180 },
  { key: '90', label: '90D', days: 90 },
  { key: '30', label: '30D', days: 30 },
];

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);
  return `${sign}$${absolute.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;
};

const formatCompactCurrency = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);

  if (absolute >= 1000000) {
    return `${sign}$${(absolute / 1000000).toFixed(1)}M`;
  }

  if (absolute >= 1000) {
    return `${sign}$${Math.round(absolute / 1000)}K`;
  }

  return `${sign}$${Math.round(absolute)}`;
};

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

const buildReadout = (point, fallbackSeries) => {
  const latest = point ?? fallbackSeries?.[fallbackSeries.length - 1];
  if (!latest) {
    return {
      date: 'Loading',
      value: '—',
      tone: 'neutral',
    };
  }

  const tone = latest.cumulative > 0 ? 'positive' : latest.cumulative < 0 ? 'negative' : 'neutral';

  return {
    date: formatDate(latest.date),
    value: formatCurrency(latest.cumulative),
    tone,
  };
};

function Home1PositionChart({ mainSeries, theoreticalSeries }) {
  const containerRef = useRef(null);
  const chartIdRef = useRef(`home1-position-${Math.random().toString(36).slice(2)}`);
  const [rangeKey, setRangeKey] = useState('all');
  const [showHighWaterMark, setShowHighWaterMark] = useState(true);
  const [readout, setReadout] = useState(() => buildReadout(null, mainSeries));

  const activeMainSeries = useMemo(() => {
    if (!mainSeries?.length) return [];

    const selectedRange = RANGE_OPTIONS.find((option) => option.key === rangeKey);
    if (!selectedRange?.days) return mainSeries;

    const latestDate = mainSeries[mainSeries.length - 1].date;
    const cutoff = new Date(latestDate);
    cutoff.setDate(cutoff.getDate() - selectedRange.days);

    const filtered = mainSeries.filter((entry) => entry.date >= cutoff);
    return filtered.length >= 2 ? filtered : mainSeries.slice(-Math.min(mainSeries.length, 2));
  }, [mainSeries, rangeKey]);

  const activeTheoreticalSeries = useMemo(() => {
    if (!showHighWaterMark || !theoreticalSeries?.length || !activeMainSeries.length) return [];
    const startDate = activeMainSeries[0].date;
    const endDate = activeMainSeries[activeMainSeries.length - 1].date;
    return theoreticalSeries.filter((entry) => entry.date >= startDate && entry.date <= endDate);
  }, [activeMainSeries, showHighWaterMark, theoreticalSeries]);

  useEffect(() => {
    setReadout(buildReadout(null, activeMainSeries));
  }, [activeMainSeries]);

  useEffect(() => {
    let cancelled = false;
    let resizeObserver;
    let resizeHandler;
    const container = containerRef.current;

    if (!container) return undefined;

    async function setupChart() {
      if (!activeMainSeries?.length) {
        container.innerHTML = '';
        return;
      }

      const d3 = await loadExternalScript(D3_URL, 'd3');
      if (cancelled) return;

      const draw = () => {
        const containerWidth = Math.max(container.clientWidth || 0, 320);
        const isSmall = containerWidth < 680;
        const width = containerWidth;
        const height = Math.round(
          isSmall
            ? Math.min(410, Math.max(340, containerWidth * 0.92))
            : Math.min(520, Math.max(430, containerWidth * 0.54))
        );
        const margin = {
          top: isSmall ? 14 : 16,
          right: isSmall ? 12 : 18,
          bottom: isSmall ? 52 : 60,
          left: isSmall ? 58 : 78,
        };
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;
        const horizontalPlotInset = Math.min(10, Math.max(7, chartWidth * 0.018));
        const verticalPlotInset = Math.min(10, Math.max(7, chartHeight * 0.025));
        const includeTheoretical = activeTheoreticalSeries?.length > 1;
        const id = chartIdRef.current;
        const reduceMotion =
          typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const clearStrokeAnimation = (path) =>
          path.interrupt().attr('stroke-dasharray', null).attr('stroke-dashoffset', null);

        container.innerHTML = '';

        const svg = d3
          .select(container)
          .append('svg')
          .attr('class', 'home1-position-chart__svg')
          .attr('viewBox', `0 0 ${width} ${height}`)
          .attr('role', 'img')
          .attr('aria-label', 'Position so far line chart')
          .attr('preserveAspectRatio', 'xMidYMid meet');

        const defs = svg.append('defs');

        defs
          .append('clipPath')
          .attr('id', `${id}-clip`)
          .append('rect')
          .attr('x', -horizontalPlotInset)
          .attr('y', -verticalPlotInset)
          .attr('width', chartWidth + horizontalPlotInset * 2)
          .attr('height', chartHeight + verticalPlotInset * 2);

        const mainGradient = defs
          .append('linearGradient')
          .attr('id', `${id}-main-area`)
          .attr('x1', '0%')
          .attr('x2', '0%')
          .attr('y1', '0%')
          .attr('y2', '100%');

        mainGradient.append('stop').attr('offset', '0%').attr('stop-color', '#ffffff').attr('stop-opacity', 0.46);
        mainGradient.append('stop').attr('offset', '100%').attr('stop-color', '#ffffff').attr('stop-opacity', 0.03);

        const greenGradient = defs
          .append('linearGradient')
          .attr('id', `${id}-green-area`)
          .attr('x1', '0%')
          .attr('x2', '0%')
          .attr('y1', '0%')
          .attr('y2', '100%');

        greenGradient.append('stop').attr('offset', '0%').attr('stop-color', '#21c45d').attr('stop-opacity', 0.3);
        greenGradient.append('stop').attr('offset', '100%').attr('stop-color', '#21c45d').attr('stop-opacity', 0);

        const plot = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        const dateExtent = includeTheoretical
          ? [
              d3.min([d3.min(activeMainSeries, (d) => d.date), d3.min(activeTheoreticalSeries, (d) => d.date)]),
              d3.max([d3.max(activeMainSeries, (d) => d.date), d3.max(activeTheoreticalSeries, (d) => d.date)]),
            ]
          : d3.extent(activeMainSeries, (d) => d.date);

        const valueExtent = includeTheoretical
          ? [
              d3.min([d3.min(activeMainSeries, (d) => d.cumulative), d3.min(activeTheoreticalSeries, (d) => d.hwm), 0]),
              d3.max([d3.max(activeMainSeries, (d) => d.cumulative), d3.max(activeTheoreticalSeries, (d) => d.hwm), 0]),
            ]
          : [
              d3.min([d3.min(activeMainSeries, (d) => d.cumulative), 0]),
              d3.max([d3.max(activeMainSeries, (d) => d.cumulative), 0]),
            ];

        const xSpanDays = (dateExtent[1] - dateExtent[0]) / 86400000;
        const yPadding = Math.max((valueExtent[1] - valueExtent[0]) * (includeTheoretical ? 0.055 : 0.08), 1);
        const x = d3.scaleTime().domain(dateExtent).range([horizontalPlotInset, chartWidth - horizontalPlotInset]);
        const y = d3
          .scaleLinear()
          .domain([valueExtent[0] - yPadding, valueExtent[1] + yPadding])
          .range([chartHeight - verticalPlotInset, verticalPlotInset]);

        const tickCount = isSmall ? 4 : 7;
        const buildXAxis = (scale) =>
          d3
            .axisBottom(scale)
            .ticks(tickCount)
            .tickFormat(d3.timeFormat(xSpanDays > 120 ? "%b '%y" : '%b %d'));
        const yAxis = d3.axisLeft(y).ticks(isSmall ? 5 : 6).tickFormat(formatCompactCurrency);

        plot
          .append('g')
          .attr('class', 'home1-position-chart__grid')
          .call(d3.axisLeft(y).ticks(isSmall ? 5 : 6).tickSize(-chartWidth).tickFormat(''));

        plot
          .append('g')
          .attr('class', 'home1-position-chart__axis home1-position-chart__axis--y')
          .call(yAxis);

        const xAxisGroup = plot
          .append('g')
          .attr('class', 'home1-position-chart__axis home1-position-chart__axis--x')
          .attr('transform', `translate(0,${chartHeight})`)
          .call(buildXAxis(x));

        xAxisGroup.selectAll('text').attr('dy', '1.1em');

        plot
          .append('line')
          .attr('class', 'home1-position-chart__zero')
          .attr('x1', 0)
          .attr('x2', chartWidth)
          .attr('y1', y(0))
          .attr('y2', y(0));

        const chartLayer = plot.append('g').attr('clip-path', `url(#${id}-clip)`);
        const lineGenerator = (scale) =>
          d3
            .line()
            .curve(d3.curveMonotoneX)
            .x((d) => scale(d.date))
            .y((d) => y(d.cumulative));
        const areaGenerator = (scale) =>
          d3
            .area()
            .curve(d3.curveMonotoneX)
            .x((d) => scale(d.date))
            .y0(y(0))
            .y1((d) => y(d.cumulative));

        const areaPath = chartLayer
          .append('path')
          .datum(activeMainSeries)
          .attr('class', 'home1-position-chart__area')
          .attr('fill', `url(#${id}-main-area)`)
          .attr('d', areaGenerator(x))
          .attr('opacity', 0);

        areaPath
          .transition()
          .duration(reduceMotion ? 0 : 720)
          .ease(d3.easeCubicOut)
          .attr('opacity', 1);

        const linePath = chartLayer
          .append('path')
          .datum(activeMainSeries)
          .attr('class', 'home1-position-chart__line')
          .attr('d', lineGenerator(x));

        const lineLength = linePath.node()?.getTotalLength() ?? 0;
        linePath
          .attr('stroke-dasharray', lineLength)
          .attr('stroke-dashoffset', lineLength)
          .transition()
          .duration(reduceMotion ? 0 : 1300)
          .ease(d3.easeCubicOut)
          .attr('stroke-dashoffset', 0)
          .on('end interrupt', function clearMainLineDash() {
            d3.select(this).attr('stroke-dasharray', null).attr('stroke-dashoffset', null);
          });

        let theoreticalAreaPath = null;
        let theoreticalPath = null;
        let theoreticalAreaGenerator = null;
        let theoreticalLineGenerator = null;

        if (includeTheoretical) {
          theoreticalLineGenerator = (scale) =>
            d3
              .line()
              .curve(d3.curveMonotoneX)
              .x((d) => scale(d.date))
              .y((d) => y(d.hwm));
          theoreticalAreaGenerator = (scale) =>
            d3
              .area()
              .curve(d3.curveMonotoneX)
              .x((d) => scale(d.date))
              .y0(y(0))
              .y1((d) => y(d.hwm));

          theoreticalAreaPath = chartLayer
            .append('path')
            .datum(activeTheoreticalSeries)
            .attr('class', 'home1-position-chart__theoretical-area')
            .attr('fill', `url(#${id}-green-area)`)
            .attr('d', theoreticalAreaGenerator(x))
            .attr('opacity', 0);

          theoreticalAreaPath
            .transition()
            .delay(reduceMotion ? 0 : 340)
            .duration(reduceMotion ? 0 : 820)
            .ease(d3.easeCubicOut)
            .attr('opacity', 1);

          theoreticalPath = chartLayer
            .append('path')
            .datum(activeTheoreticalSeries)
            .attr('class', 'home1-position-chart__theoretical-line')
            .attr('d', theoreticalLineGenerator(x));

          const theoreticalLength = theoreticalPath.node()?.getTotalLength() ?? 0;
          theoreticalPath
            .attr('stroke-dasharray', theoreticalLength)
            .attr('stroke-dashoffset', theoreticalLength)
            .transition()
            .delay(reduceMotion ? 0 : 260)
            .duration(reduceMotion ? 0 : 1200)
            .ease(d3.easeCubicOut)
            .attr('stroke-dashoffset', 0)
            .on('end interrupt', function clearTheoreticalLineDash() {
              d3.select(this).attr('stroke-dasharray', null).attr('stroke-dashoffset', null);
            });
        }

        const latest = activeMainSeries[activeMainSeries.length - 1];
        const latestRing = chartLayer
          .append('circle')
          .attr('class', 'home1-position-chart__latest-ring')
          .attr('cx', x(latest.date))
          .attr('cy', y(latest.cumulative))
          .attr('r', 11);

        const latestDot = chartLayer
          .append('circle')
          .attr('class', 'home1-position-chart__latest-dot')
          .attr('cx', x(latest.date))
          .attr('cy', y(latest.cumulative))
          .attr('r', 5);

        const focus = plot.append('g').attr('class', 'home1-position-chart__focus').style('opacity', 0);
        focus
          .append('line')
          .attr('class', 'home1-position-chart__focus-line')
          .attr('y1', 0)
          .attr('y2', chartHeight);
        focus.append('circle').attr('class', 'home1-position-chart__focus-dot').attr('r', 6);

        const bisectDate = d3.bisector((d) => d.date).left;
        let activeX = x;
        const pickPoint = (event) => {
          const [pointerX] = d3.pointer(event);
          const hoveredDate = activeX.invert(Math.max(0, Math.min(chartWidth, pointerX)));
          const index = bisectDate(activeMainSeries, hoveredDate, 1);
          const previous = activeMainSeries[index - 1];
          const next = activeMainSeries[index];
          return !next || hoveredDate - previous.date < next.date - hoveredDate ? previous : next;
        };

        const overlay = plot
          .append('rect')
          .attr('class', 'home1-position-chart__overlay')
          .attr('width', chartWidth)
          .attr('height', chartHeight);

        overlay
          .on('pointerenter pointermove', (event) => {
            const point = pickPoint(event);
            focus
              .style('opacity', 1)
              .attr('transform', `translate(${activeX(point.date)},0)`);
            focus.select('circle').attr('cy', y(point.cumulative));
            setReadout(buildReadout(point, activeMainSeries));
          })
          .on('pointerleave', () => {
            focus.style('opacity', 0);
            setReadout(buildReadout(null, activeMainSeries));
          });

        const zoom = d3
          .zoom()
          .scaleExtent([1, 14])
          .extent([
            [0, 0],
            [chartWidth, chartHeight],
          ])
          .translateExtent([
            [0, 0],
            [chartWidth, chartHeight],
          ])
          .on('zoom', (event) => {
            activeX = event.transform.rescaleX(x);
            xAxisGroup.call(buildXAxis(activeX));
            xAxisGroup.selectAll('text').attr('dy', '1.1em');
            areaPath.attr('d', areaGenerator(activeX));
            clearStrokeAnimation(linePath).attr('d', lineGenerator(activeX));
            if (theoreticalAreaPath && theoreticalAreaGenerator) {
              theoreticalAreaPath.attr('d', theoreticalAreaGenerator(activeX));
            }
            if (theoreticalPath && theoreticalLineGenerator) {
              clearStrokeAnimation(theoreticalPath).attr('d', theoreticalLineGenerator(activeX));
            }
            latestRing.attr('cx', activeX(latest.date));
            latestDot.attr('cx', activeX(latest.date));
          });

        overlay.call(zoom);
      };

      draw();

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(draw);
        resizeObserver.observe(container);
      } else {
        resizeHandler = draw;
        window.addEventListener('resize', resizeHandler);
      }
    }

    setupChart();

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      container.innerHTML = '';
    };
  }, [activeMainSeries, activeTheoreticalSeries]);

  if (!mainSeries?.length) {
    return <div className="home1-position-chart__empty">Position data is loading.</div>;
  }

  return (
    <div className="home1-position-chart">
      <div className="home1-position-chart__toolbar" role="group" aria-label="Position chart drill range">
        <div className="home1-position-chart__range-buttons">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={rangeKey === option.key ? 'active' : ''}
              onClick={() => setRangeKey(option.key)}
              aria-pressed={rangeKey === option.key}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`home1-position-chart__hwm-toggle${showHighWaterMark ? ' active' : ''}`}
          onClick={() => setShowHighWaterMark((current) => !current)}
          aria-pressed={showHighWaterMark}
        >
          Theoretical High Water Mark
        </button>
      </div>
      <div ref={containerRef} className="home1-position-chart__canvas" />
      <div className="home1-position-chart__readout" aria-live="polite">
        <span>{readout.date}</span>
        <strong className={readout.tone}>{readout.value}</strong>
      </div>
    </div>
  );
}

export default Home1PositionChart;
